"use client";
import { Card } from "@workspace/ui/components/card";
import {
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { safeSetTimeout } from "@/lib/utils/safe-timeout";
import type { SimpleArtist } from "@/components/features/artist/cards/artist-discovery-card";
import { ArtistGrid } from "@/components/features/artist/discovery/artist-grid";
import { ArtistDiscoverySkeleton } from "@/components/features/artist/skeletons";
import { SearchEmptyState } from "@/components/features/artist/states";
import { GenreFilter } from "@/components/shared";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import {
	ARTIST_SORT_OPTIONS,
	UniversalSortDropdown,
} from "@/components/shared/filters/search/universal-sort-dropdown";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	useArtistGenres,
	useArtistsInfinite,
} from "@/hooks/features/artist/data/migration";
import { useSearchInput } from "@/hooks/shared";
import { useResponsiveArtistLimit } from "@/hooks/shared/use-responsive-limit";
import { useInfiniteScroll } from "@/hooks/shared/use-infinite-scroll";

export function ArtistsPageClient() {
	// Get responsive limits for optimal performance on different screen sizes
	const { initialLimit } = useResponsiveArtistLimit();

	// State
	const [sortBy, setSortBy] = useState<"popularity" | "alpha" | "alpha-desc">(
		"popularity",
	);
	const [selectedGenre, setSelectedGenre] = useState<string>("all");
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	// Use the new search hook
	const search = useSearchInput({
		debounceMs: 300,
	});

	// Use infinite query hook with stable parameters - ensure consistent object reference
	const queryParams = useMemo(() => {
		return {
			search: search.debouncedValue,
			genre: selectedGenre,
			sort: sortBy,
			limit: initialLimit,
		};
	}, [search.debouncedValue, selectedGenre, sortBy, initialLimit]);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		error,
	} = useArtistsInfinite(queryParams);

	// Flatten all pages into single array
	const artists = useMemo(() => {
		const typedData = data as
			| { pages?: Array<{ items: SimpleArtist[] }> }
			| undefined;
		if (!typedData?.pages) return [];
		return typedData.pages.flatMap((page) => page.items);
	}, [data]);

	// Fetch available genres using tRPC (independent loading)
	const { data: genreData } = useArtistGenres();

	// Use genres from tRPC
	const typedGenreData = genreData as
		| { genres?: string[]; hasNoGenreArtists?: boolean }
		| undefined;
	const availableGenres = typedGenreData?.genres || [];
	const hasNoGenreArtists = typedGenreData?.hasNoGenreArtists || false;

	// Set initial load to false when we have data
	// With ISR, data is immediately available, so no skeleton needed
	useEffect(() => {
		// If we already have data (from SSR/ISR), don't show skeleton
		if (data) {
			setIsInitialLoad(false);
			return;
		}
		
		// Only show skeleton if we're actually loading without data
		if (!isLoading) {
			const timer = safeSetTimeout(() => {
				// Use React 18 startTransition for non-urgent update
				startTransition(() => {
					setIsInitialLoad(false);
				});
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isLoading, data]);

	// Stable callback handlers
	const handleGenreChange = useCallback((genre: string) => {
		setSelectedGenre(genre);
	}, []);

	const handleSortChange = useCallback((value: string) => {
		setSortBy(value as "popularity" | "alpha" | "alpha-desc");
	}, []);

	// Enable infinite scroll
	useInfiniteScroll({
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isActive: true,
		threshold: 800,
	});

	if (error) {
		return (
			<div className="mx-auto flex max-w-3xl flex-1 flex-col p-3 pt-0">
				<Card className="p-8 text-center">
					<h3 className="mb-2 font-semibold text-xl">Error Loading Artists</h3>
					<p className="text-muted-foreground text-sm">
						Failed to load artists. Please try refreshing the page.
					</p>
				</Card>
			</div>
		);
	}

	return (
		<div className="mx-auto flex max-w-3xl flex-1 flex-col p-3 pt-0">
			{/* Sticky Search and Filter Controls */}
			<div className="sticky top-14 z-30 bg-background py-3">
				<div className="space-y-3">
					<div className="grid grid-cols-2 gap-3">
						<div className="w-full">
							<GenreFilter
								value={selectedGenre}
								onValueChange={handleGenreChange}
								availableGenres={availableGenres}
								placeholder="All Genres"
								hasNoGenreArtists={hasNoGenreArtists}
							/>
						</div>
						<div className="w-full">
							<UniversalSortDropdown
								value={sortBy}
								onValueChange={handleSortChange}
								options={ARTIST_SORT_OPTIONS}
								preserveScrollPosition={true}
							/>
						</div>
					</div>
					<EnhancedSearchInput
						value={search.value}
						onValueChange={search.setValue}
						placeholder="Search artists..."
						showLoadingIndicator={true}
						isLoading={search.isSearching}
					/>
				</div>
			</div>

			{/* Artists Grid */}
			{(isInitialLoad && isLoading) || (isLoading && !data) ? (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
					{[...Array(initialLimit)].map((_, i) => (
						<ArtistDiscoverySkeleton key={`loading-${i}`} />
					))}
				</div>
			) : artists.length > 0 ? (
				<>
					<ArtistGrid artists={artists} />
					{/* Loading indicator for infinite scroll */}
					{isFetchingNextPage && (
						<div className="flex justify-center py-4">
							<Spinner variant="muted" />
						</div>
					)}
				</>
			) : (
				<SearchEmptyState
					searchTerm={search.value}
					selectedGenre={selectedGenre}
					onClearSearch={() => search.setValue("")}
					onClearGenre={() => setSelectedGenre("all")}
				/>
			)}
		</div>
	);
}
